import { deflate } from 'pako';
import Config from '../config';
import {
  handleErrors,
  parseAsJson,
} from '../utils/requestHelpers';
import mapFields from '../utils/fieldMapping';
import fetch from '../utils/fetchService';
import { NO_FLAG } from './constants';

export const fetchAppData = async () => await fetch(
  Config.endpoints.APP_DATA
)
  .then(handleErrors({ errorMessage: 'Fetching Application data failed' }))
  .then(parseAsJson);

export const fetchProvisions = async period => await fetch(
  Config.endpoints.PROVISIONS
    .replace(/:periodName/, period.name)
)
  .then(handleErrors({ errorMessage: 'Fetching provisions failed' }))
  .then(parseAsJson);

export const fetchPeriods = async () => await fetch(Config.endpoints.PERIODS)
  .then(handleErrors({ errorMessage: 'Fetching periods failed' }))
  .then(parseAsJson);

export const fetchProvisionData = async ({ companyCode, period }) => await fetch(
  Config.endpoints.PROVISION
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
)
  .then(handleErrors({
    errorMessage: 'Fetching provision data failed',
    parseResponseErrorMessage: true,
  }))
  .then(parseAsJson)
  .then(provisionData => ({
    ...provisionData,
    calcResultsLog: provisionData.calcResultsLog.map(mapFields({
      CALC_JOB_ID: 'calcJobId',
      JOB_ID: 'jobId',
      LOG_ENTITIES: 'logEntities',
      LOG_ID: 'logId',
      LOG_TEXT: 'logText',
      LOG_TYPE: 'logType',
      PERF_MONITOR: 'perfMonitor',
    })),
  }));

export const calculateProvision = async ({ businessUnit, companyCode, period }) => await fetch(
  Config.endpoints.CALCULATE_PROVISION
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
    .replace(/:businessUnit/, businessUnit)
)
  .then(handleErrors({ errorMessage: 'Calculating provision failed' }))
  .then(parseAsJson);

export const proformaCalc = async ({ companyCode, period }) => await fetch(
  Config.endpoints.PROFORMA_CALC
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
)
  .then(handleErrors({ errorMessage: 'Calculating provision failed' }))
  .then(parseAsJson);

export const uploadBalanceFile = async ({
  companyCode,
  period,
  file,
  isBeginningBalance = NO_FLAG,
}) => await fetch(
  Config.endpoints.UPLOAD_BALANCE_FILE
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
    .replace(/:isBeginningBalance/, isBeginningBalance),
  {
    method: 'PUT',
    body: deflate(JSON.stringify(file)),
    headers: {
      'Content-Encoding': 'deflate',
    },
  }
)
  .then(handleErrors({ errorMessage: 'Trial Balance File Upload failed' }));

export const validateBalanceFile = async ({
  file,
  isBeginningBalance,
}) => await fetch(
  Config.endpoints.VALIDATE_BALANCE_FILE
    .replace(/:isBeginningBalance/, isBeginningBalance),
  {
    method: 'POST',
    body: deflate(JSON.stringify(file)),
    headers: {
      'Content-Encoding': 'deflate',
    },
  }
)
  .then(handleErrors({ errorMessage: 'Balance File validation failed' }))
  .then(parseAsJson);

export const fetchTaxYears = async () => await fetch(Config.endpoints.TAX_YEARS)
  .then(handleErrors({ errorMessage: 'Fetching Tax Years failed' }))
  .then(parseAsJson);

const mapAdjustmentData = (adjustmentData, companyCode) => {
  adjustmentData.bookToTaxDescription = adjustmentData.bookToTaxDescription
    && adjustmentData.bookToTaxDescription.toString();
  return mapFields({
    account: 'account',
    adjustmentType: 'adjustmentType',
    amount: 'amount',
    amountFrom: 'amountFrom',
    annualCum: 'annualCum',
    bookType: 'bookType',
    bookToTaxDescription: 'bookToTaxDescription',
    companyCode: 'companyCode',
    countryTaxType: 'countryTaxType',
    description: 'description',
    frequency: 'frequency',
    multiplier: 'multiplier',
  })({
    companyCode,
    ...adjustmentData,
  });
};

export const addAdjustment = async ({ companyCode, period, adjustmentData }) => {
  adjustmentData.bookToTaxDescription = adjustmentData.bookToTaxDescription
    && adjustmentData.bookToTaxDescription.toString();
  const adjustmentDataMapped = mapAdjustmentData(adjustmentData, companyCode);

  return await fetch(
    Config.endpoints.ADD_ADJUSTMENT
      .replace(':period', period), {
      method: 'POST',
      body: JSON.stringify(adjustmentDataMapped),
    }
  )
    .then(handleErrors({ errorMessage: 'Adding adjustment failed' }))
    .then(parseAsJson);
};

export const updateAdjustment = async ({ companyCode, period, adjustmentData, adjustmentId }) => {
  const adjustmentDataMapped = mapAdjustmentData(adjustmentData, companyCode);

  return await fetch(
    Config.endpoints.UPDATE_ADJUSTMENT
      .replace(':adjustmentId', adjustmentId)
      .replace(':period', period), {
      method: 'PUT',
      body: JSON.stringify(adjustmentDataMapped),
    }
  )
    .then(handleErrors({ errorMessage: 'Updating adjustment failed' }));
};

export const removeAdjustment = async ({ period, companyCode, adjustmentId }) => await fetch(
  Config.endpoints.REMOVE_ADJUSTMENT
    .replace(':adjustmentId', adjustmentId)
    .replace(':companyCode', companyCode)
    .replace(':period', period), {
    method: 'DELETE',
  }
)
  .then(handleErrors({ errorMessage: 'Removing adjustment failed' }));

export const fetchAdjustment = async ({ adjustmentId, period }) => await fetch(
  Config.endpoints.FETCH_ADJUSTMENT
    .replace(':adjustmentId', adjustmentId)
    .replace(':period', period)
)
  .then(handleErrors({ errorMessage: 'Fetching adjustment failed' }))
  .then(parseAsJson);

export const addAttachment = async ({ name, type, image }) => {
  const attachmentsDataMapped = {
    FILE_NAME: name,
    FILE_TYPE: type,
    FILE_IMAGE: image,
  };

  return await fetch(
    Config.endpoints.ADD_ATTACHMENT,
    {
      method: 'POST',
      body: JSON.stringify(attachmentsDataMapped),
    }
  )
    .then(handleErrors({ errorMessage: `Adding attachment failed - ${name}` }))
    .then(parseAsJson);
};

export const fetchAccounts = async () => await fetch(Config.endpoints.ACCOUNTS)
  .then(handleErrors({ errorMessage: 'Fetching accounts failed' }))
  .then(parseAsJson);

export const fetchCompanyCodeDetails = async ({ companyCode, period }) => await fetch(
  Config.endpoints.COMPANY_CODE_DETAILS
    .replace(/:companyCode/, companyCode)
    .replace(/:period/, period)
)
  .then(handleErrors({
    errorMessage: 'Fetching Company Code Details failed',
    parseResponseErrorMessage: true,
  }))
  .then(parseAsJson);

export const fetchBookTypes = async ({ period }) => await fetch(
  Config.endpoints.BOOK_TYPES
    .replace(/:period/, period)
)
  .then(handleErrors({ errorMessage: 'Fetching Book Types failed' }))
  .then(parseAsJson);

export const fetchAdjustmentAttachments = async ({ period, adjustmentId }) => await fetch(
  Config.endpoints.FETCH_ADJUSTMENT_ATTACHMENTS
    .replace(':adjustmentId', adjustmentId)
    .replace(':period', period)
)
  .then(handleErrors({ errorMessage: 'Fetching attachments failed' }))
  .then(parseAsJson);

export const fetchUserProfile = async () => await fetch(
  Config.endpoints.FETCH_USER_PROFILE
)
  .then(handleErrors({ errorMessage: 'Fetching user profile failed' }))
  .then(parseAsJson);

export const logout = async () => await fetch(
  Config.endpoints.LOGOUT
)
  .then(handleErrors({ errorMessage: 'Logout failed' }));

export const fetchConnections = async ({
  period,
}) => await fetch(
  Config.endpoints.FETCH_CONNECTIONS
    .replace(/:period/, period)
)
  .then(handleErrors({ errorMessage: 'Fetching Connections failed' }))
  .then(parseAsJson);

export const fetchConnectionsByType = async ({
  period,
  connType,
}) => await fetch(
  Config.endpoints.FETCH_CONNECTIONS_BY_TYPE
    .replace(/:period/, period)
    .replace(/:connType/, connType)
)
  .then(handleErrors({ errorMessage: 'Fetching Connections by Type failed' }))
  .then(parseAsJson);

export const fetchAssignedConnections = async ({
  period,
}) => await fetch(
  Config.endpoints.FETCH_ASSIGNED_CONNECTIONS
    .replace(/:period/, period)
)
  .then(handleErrors({ errorMessage: 'Fetching assigned Connections failed' }))
  .then(parseAsJson);

export const fetchUserClientList = () => fetch(
  Config.endpoints.FETCH_USER_CLIENT_LIST
)
  .then(handleErrors({ errorMessage: 'Fetching User\'s client list failed' }))
  .then(parseAsJson);

export const fetchReportingCurrencyCodes = () => fetch(
  Config.endpoints.FETCH_REPORTING_CURRENCY_CODES
)
  .then(handleErrors({ errorMessage: 'Fetching Reporting Currency Codes failed' }))
  .then(parseAsJson);

export const addAttachments = async files => {
  const fieldMapper = mapFields({
    fileName: 'FILE_NAME',
    fileType: 'FILE_TYPE',
    fileImage: 'FILE_IMAGE',
    name: 'FILE_NAME',
    type: 'FILE_TYPE',
    image: 'FILE_IMAGE',
    id: 'index',
  });

  const attachmentsDataMapped = files.map(fieldMapper);

  return await fetch(
    Config.endpoints.ADD_ATTACHMENTS,
    {
      method: 'POST',
      body: JSON.stringify(attachmentsDataMapped),
    }
  )
    .then(handleErrors({ errorMessage: `Adding attachments failed` }))
    .then(parseAsJson);
};

export const isCountryTaxTypeSubdivision = async countryTaxType => await fetch(
  Config.endpoints.IS_SUBDIVISION
    .replace(/:countryTaxType/, countryTaxType)
)
  .then(handleErrors({ errorMessage: 'Checking subdivision failed' }))
  .then(parseAsJson);

export const fetchCountryTaxTypeDescriptions = async ({ periodName }) => await fetch(
  Config.endpoints.FETCH_COUNTRY_TAX_TYPE_DESCRIPTIONS
    .replace(/:periodName/, periodName)
)
  .then(handleErrors({ errorMessage: 'Fetching country tax type descriptions failed' }))
  .then(parseAsJson);

export const checkSessionStatus = async () => await fetch(
  Config.endpoints.GET_SESSION_STATUS
)
  .then(handleErrors({ errorMessage: 'Checking session status failed' }))
  .then(parseAsJson);

export const updateSessionToken = async () => await fetch(
  Config.endpoints.UPDATE_SESSION_TOKEN
)
  .then(handleErrors({ errorMessage: 'Updating session token failed' }))
  .then(parseAsJson);

export const fetchCategories = async periodName =>
  await fetch(
    Config.endpoints.FETCH_CATEGORIES_LIST.replace(/:periodName/, periodName)
  )
    .then(
      handleErrors({
        errorMessage: 'Fetching Categories failed',
        parseResponseErrorMessage: true,
      })
    )
    .then(parseAsJson);

export const fetchYearStartPeriod = async () => await fetch(
  Config.endpoints.YEAR_START_PERIOD
)
  .then(handleErrors({ errorMessage: 'Error fetching year start period.' }))
  .then(parseAsJson);

export const saveArchiveReport = async (batchId, payload) => await fetch(
  Config.endpoints.SAVE_ARCHIVE_REPORT
    .replace(':batchId', batchId), {
    method: 'POST',
    body: JSON.stringify(payload),
  }
)
  .then(handleErrors({ errorMessage: 'Adding adjustment failed' }))
  .then(parseAsJson);

export const fetchCalculationId = async (
  connName,
  periodName
) => await fetch(
  Config.endpoints.FETCH_CALCULATION_ID
    .replace(/:connName/, connName)
    .replace(/:periodName/, periodName)
)
  .then(handleErrors({ errorMessage: 'Fetching calculation id failed' }))
  .then(parseAsJson);

export const fetchTrialBalanceCalculationId = async (
  connName,
  periodName
) => await fetch(
  Config.endpoints.FETCH_TRIAL_BALANCE_CALCULATION_ID
    .replace(/:connName/, connName)
    .replace(/:periodName/, periodName)
)
  .then(handleErrors({ errorMessage: 'Fetching trial balance calculation id failed' }))
  .then(parseAsJson);

export const fetchJournalFilename = async (
  connName,
  periodName,
  calcId
) => await fetch(
  Config.endpoints.FETCH_JOURNAL_FILENAME
    .replace(/:connName/, connName)
    .replace(/:periodName/, periodName)
    .replace(/:calcId/, calcId)
)
  .then(handleErrors({ errorMessage: 'Fetching Journal Filename failed' }))
  .then(parseAsJson);

export const fetchGenerateHistoricalTrialBalance = async calcId => await fetch(
  Config.endpoints.FETCH_HISTORICAL_TRIAL_BALANCE_DATA.replace(/:calcId/, calcId)
)
  .then(handleErrors({ errorMessage: 'Fetching historical trial balance failed' }))
  .then(parseAsJson);

